// client/src/components/ticketing/PromoCodeManager.jsx
//
// CRUD panel for ticket promo codes on a ticketed event item
// (server/routes/ticketingRoutes.js — t_ticket_promo_code). Rendered as a
// tab inside TicketConfig.jsx.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const EMPTY_PROMO = {
  code: "",
  discount_type: "percent",
  discount_value: "10",
  max_uses: "",
  valid_until: "",
};

export default function PromoCodeManager({ tenantId, eventId, itemId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY_PROMO);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const loadCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/promo-codes`);
      if (data.success) setCodes(data.promoCodes);
    } catch (err) {
      console.error("Error loading promo codes:", err);
    }
  }, [base]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const submitCode = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: "", type: "" });

    const payload = {
      code: form.code.trim(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      max_uses: form.max_uses === "" ? null : Number(form.max_uses),
      valid_until: form.valid_until || null,
    };

    try {
      const { data } = await axios.post(`${base}/promo-codes`, payload);
      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        setForm(EMPTY_PROMO);
        loadCodes();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to create promo code", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm("Delete this promo code?")) return;
    try {
      await axios.delete(`${base}/promo-codes/${codeId}`);
      loadCodes();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete promo code", type: "error" });
    }
  };

  return (
    <div>
      {message.text && (
        <div
          className={`mb-4 px-3 py-2 rounded text-sm ${
            message.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <form
        onSubmit={submitCode}
        className="rounded-xl border border-black/10 dark:border-white/10 p-4 mb-4 grid grid-cols-2 gap-3"
      >
        <input
          className="px-3 py-2 rounded border bg-transparent uppercase"
          placeholder="Code (e.g. EARLYBIRD10)"
          value={form.code}
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
          required
        />
        <select
          className="px-3 py-2 rounded border bg-transparent"
          value={form.discount_type}
          onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
        >
          <option value="percent">Percent off</option>
          <option value="flat">Flat amount off</option>
        </select>
        <input
          className="px-3 py-2 rounded border bg-transparent"
          type="number"
          min="0"
          step="0.01"
          placeholder={form.discount_type === "percent" ? "Percent (0-100)" : "Amount off"}
          value={form.discount_value}
          onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
          required
        />
        <input
          className="px-3 py-2 rounded border bg-transparent"
          type="number"
          min="1"
          placeholder="Max uses (blank = unlimited)"
          value={form.max_uses}
          onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
        />
        <input
          className="col-span-2 px-3 py-2 rounded border bg-transparent"
          type="datetime-local"
          value={form.valid_until}
          onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
        />
        <div className="col-span-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded bg-cobalt text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : "Add Promo Code"}
          </button>
        </div>
      </form>

      {codes.length === 0 ? (
        <div className="text-sm opacity-70">No promo codes yet.</div>
      ) : (
        <ul className="space-y-2">
          {codes.map((code) => (
            <li
              key={code.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-black/10 dark:border-white/10 p-3 text-sm"
            >
              <div>
                <div className="font-semibold">{code.code}</div>
                <div className="opacity-70">
                  {code.discount_type === "percent"
                    ? `${Number(code.discount_value)}% off`
                    : `${Number(code.discount_value).toFixed(2)} off`}{" "}
                  · {code.uses_count}/{code.max_uses ?? "∞"} used
                </div>
              </div>
              <button
                onClick={() => deleteCode(code.id)}
                className="px-2 py-1 rounded border hover:bg-black/5 dark:hover:bg-white/10 text-red-600"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
